import { useState, type ReactNode } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { useWidgetDensity } from '../grid/WidgetDensityContext'

export interface ChartFrameProps {
  /** Used to build a unique, descriptive toggle label — never a bare "View as table" repeated per tile. */
  title: string
  height?: number
  /** The chart itself (Recharts). */
  chart: ReactNode
  /** The accessible `<table>` equivalent — design-system.md §5.3 "every chart has a data-table toggle equivalent". */
  table: ReactNode
  /**
   * `micro`-density replacement for `chart` (design-system.md §5 auto-density — "collapse to a
   * sparkline or a single summary stat"). Optional: a chart type with nothing meaningful to
   * simplify further (e.g. `GaugeTile`, already a single number) just omits this and keeps
   * rendering `chart` at every density, shrunk via `height` alone.
   */
  microFallback?: ReactNode
  /** Rendered below the chart at `compact`/`full` density only (e.g. a "+N more — view as table"
   * truncation note) — never shown alongside `microFallback`, which already carries its own summary. */
  footnote?: ReactNode
}

/**
 * Every `charts/*` wrapper renders through this frame so the data-table toggle is uniform and never
 * forgotten on a new chart type. Also the one place chart-level auto-density lives (IB-311 Slice 1):
 * reads `useWidgetDensity()` (default `full` outside a `DashboardGrid` cell — see
 * `WidgetDensityContext`) to shrink the plotted height and, at `micro`, swap in `microFallback`. The
 * data-table toggle itself is NEVER removed at any density (design-system.md §5.3's a11y guarantee)
 * — only its visible label collapses to an icon, the accessible name (`aria-label`) stays complete.
 */
export function ChartFrame({ title, height = 240, chart, table, microFallback, footnote }: ChartFrameProps) {
  const [showTable, setShowTable] = useState(false)
  const { density } = useWidgetDensity()

  const effectiveHeight =
    density === 'micro' ? Math.min(height, 130) : density === 'compact' ? Math.min(height, 190) : height
  const useFallback = density === 'micro' && Boolean(microFallback)
  const toggleLabel = showTable ? `Hide data table for ${title}` : `View ${title} as a table`

  return (
    <div className="iw-chart-frame" data-density={density}>
      <div className="iw-chart-frame__toolbar">
        <button
          type="button"
          className="iw-btn iw-btn--sm iw-btn--ghost"
          aria-pressed={showTable}
          aria-label={toggleLabel}
          onClick={() => setShowTable((v) => !v)}
        >
          <Icon name="rows" size={14} />
          {density === 'full' ? toggleLabel : null}
        </button>
      </div>
      {showTable ? (
        table
      ) : (
        <>
          <div style={{ width: '100%', height: effectiveHeight }}>{useFallback ? microFallback : chart}</div>
          {!useFallback && footnote ? footnote : null}
        </>
      )}
    </div>
  )
}

export interface ChartTableColumn {
  key: string
  label: string
  align?: 'start' | 'end'
}

export interface ChartAccessibleTableProps {
  caption: string
  columns: ChartTableColumn[]
  rows: Array<Record<string, string>>
}

/** The a11y equivalent of a chart — every value a screen-reader user needs, in plain markup. */
export function ChartAccessibleTable({ caption, columns, rows }: ChartAccessibleTableProps) {
  return (
    <table className="iw-chart-table">
      <caption className="iw-visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} scope="col" style={{ textAlign: c.align === 'end' ? 'right' : 'left' }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length}>No data for the current filter.</td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align === 'end' ? 'right' : 'left' }}>
                  {row[c.key]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}
